<template>
  <v-app>
    <Notify ref="notify" />
    <pwa-install-button />
    <v-layout>
      <v-main>
        <RouterView />
      </v-main>
      <version></version>
    </v-layout>
    <IntelgxLogo/>
    <MessengerFab v-if="messengerFabRoutes.includes($route.name)" :bottom-offset="$route.name === 'onboarding' ? 96 : 20" />
  </v-app>
</template>


<script>
import IntelgxLogo from "./components/common/IntelgxLogo.vue";
import MessengerFab from "@/components/common/MessengerFab.vue";
import PwaInstallButton from "@/components/common/PwaInstallButton.vue";
import Notify from "@/components/common/notify.vue";
import { RouterView } from "vue-router";
import { mapState } from "pinia";
import { useAppStore } from "./store/app";
import { useAuthStore } from "./store/modules/auth";
import { useSettingsStore } from "./store/modules/settings";
import { AUTH_EXPIRED_EVENT } from "@/services/apiClient";
import Version from "@/components/common/Version.vue"

const FONT_SCALE_MAP = { small: 0.8, medium: 1, large: 1.25 };

export default {
  components: {
    RouterView,
    Notify,
    PwaInstallButton,
    Version,
    IntelgxLogo,
    MessengerFab
  },
  data() {
    return { messengerFabRoutes: ['landing', 'login', 'onboarding'] };
  },
  created() {
    // Apply persisted locale, theme, and font size on boot — all three
    // read from the settings store, the single source of truth.
    const settingsStore = useSettingsStore();
    this.$i18n.locale = settingsStore.locale;
    this.applyLocaleDir(this.$i18n.locale);
    this.$vuetify.theme.global.name = settingsStore.theme;
    this.applyFontScale(settingsStore.preferences.playerFontSize);

    window.addEventListener(AUTH_EXPIRED_EVENT, this.handleAuthExpired);
  },

  beforeUnmount() {
    window.removeEventListener(AUTH_EXPIRED_EVENT, this.handleAuthExpired);
  },

  watch: {
    '$i18n.locale'(val) {
      this.applyLocaleDir(val);
    },
    'settingsLocale'(val) {
      this.$i18n.locale = val;
    },
    'settingsTheme'(val) {
      this.$vuetify.theme.global.name = val;
    },
    'settingsFontSize'(val) {
      this.applyFontScale(val);
    },
  },

  methods: {
    applyLocaleDir(locale) {
      const isRtl = locale === 'ar';
      document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
      document.documentElement.lang = locale;
    },
    applyFontScale(size) {
      const scale = FONT_SCALE_MAP[size] ?? 1;
      document.documentElement.style.setProperty('--player-font-scale', scale);
    },
    // The refresh token is missing or was itself rejected — the user is
    // genuinely logged out. Fired from apiClient on any authenticated call.
    handleAuthExpired() {
      useAuthStore().logout();
      if (this.$route.name !== 'login') {
        this.$router.push({ name: 'login' });
      }
    },
  },

  computed: {
    ...mapState(useAppStore, ['showMenu', 'pageColor']),
    ...mapState(useSettingsStore, { settingsLocale: 'locale', settingsTheme: 'theme', settingsFontSize: s => s.preferences.playerFontSize }),
  }
};
</script>

<style>
html {
  font-family: "Cairo", sans-serif;
}

/* Override Vuetify typography font-family to use Cairo */
.v-application {
  font-family: "Cairo", sans-serif !important;
  letter-spacing: normal !important;
}

.v-application [class*="text-h"],
.v-application [class*="text-subtitle"],
.v-application [class*="text-body"],
.v-application [class*="text-caption"],
.v-application [class*="text-overline"] {
  font-family: "Cairo", sans-serif !important;
}

.v-application * {
  letter-spacing: normal !important;
}

[dir="rtl"] * {
  letter-spacing: 0 !important;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;

  .colored {
    background-color: v-bind('pageColor');
  }

  .amiri-regular {
    font-family: "Amiri", serif;
    font-weight: 400;
    font-style: normal;
  }
}
</style>
